'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { type CurrencyOption } from '@impact/types';
import { useTenantSlug } from '@/hooks/use-tenant-slug';
import { useDonationImpactMessage } from '@/hooks/use-donation-impact-message';
import { getTenantConfig } from '@/tenants/config';
import { AmountSelector } from './AmountSelector';

const defaultDonationAmounts = [25, 50, 100, 250];

export interface DonationAmountCardProps {
  recipientName: string;
  currency: CurrencyOption;
  donationAmounts?: number[];
  onDonate: (amount: number) => void;
  className?: string;
  /** Visual theme override. Auto-resolves from tenant slug if omitted. */
  theme?: 'light' | 'dark';
}

export function DonationAmountCard({
  recipientName,
  currency,
  donationAmounts = defaultDonationAmounts,
  onDonate,
  className = '',
  theme,
}: DonationAmountCardProps) {
  const tForm = useTranslations('donate.form');
  const tenantSlug = useTenantSlug();
  const impactMessage = useDonationImpactMessage();
  const resolvedTheme = theme ?? getTenantConfig(tenantSlug).donationTheme ?? 'dark';
  const isLight = resolvedTheme === 'light';

  const [selectedAmount, setSelectedAmount] = useState<number | null>(donationAmounts[1] || 50);
  const [customAmount, setCustomAmount] = useState('');

  const actualAmount = customAmount ? parseFloat(customAmount) || 0 : selectedAmount || 0;
  const formatCurrency = (amount: number) => `${currency.symbol}${amount.toLocaleString()}`;

  const handleAmountSelect = (amount: number) => {
    if (amount === 0) {
      setSelectedAmount(null);
    } else {
      setSelectedAmount(amount);
    }
  };

  // Light theme uses semantic `primary` tokens so each tenant's brand accent
  // colour flows through from the per-tenant CSS variables.
  const tokens = isLight
    ? {
        card: 'bg-white border-stone-200',
        heading: 'text-stone-900',
        accent: 'text-primary',
        mutedText: 'text-stone-600',
        footerText: 'text-stone-500',
        button:
          'bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-semibold',
      }
    : {
        card: 'bg-stone-900 border-stone-800',
        heading: 'text-white',
        accent: 'text-dgr-gold',
        mutedText: 'text-stone-500',
        footerText: 'text-stone-600',
        button:
          'bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light disabled:opacity-50 disabled:cursor-not-allowed font-semibold',
      };

  return (
    <div className={`${tokens.card} rounded-xl p-6 border ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Heart className={`w-5 h-5 ${tokens.accent}`} />
        <h2 className={`text-lg font-semibold ${tokens.heading}`}>
          {tForm('supportRecipient', { recipientName })}
        </h2>
      </div>

      {/* Currency Notice */}
      <p className={`text-sm mb-4 ${tokens.mutedText}`}>
        {tForm('currencyNotice', { currency: `${currency.label} (${currency.code})` })}
      </p>

      <div className="space-y-4 mb-4">
        <AmountSelector
          currency={currency}
          amounts={donationAmounts}
          selectedAmount={selectedAmount}
          customAmount={customAmount}
          onAmountSelect={handleAmountSelect}
          onCustomAmountChange={setCustomAmount}
        />
      </div>

      {/* Donate Button */}
      <Button
        type="button"
        onClick={() => {
          if (actualAmount > 0) onDonate(actualAmount);
        }}
        disabled={actualAmount <= 0}
        className={`w-full h-12 ${tokens.button}`}
      >
        <Heart className="w-4 h-4 mr-2" />
        {actualAmount > 0
          ? tForm('donateAmount', { amount: formatCurrency(actualAmount) })
          : tForm('donate')}
      </Button>

      {impactMessage ? (
        <p className={`text-center text-xs mt-3 ${tokens.footerText}`}>{impactMessage}</p>
      ) : null}
    </div>
  );
}
